import { useRef } from 'react';
import AnimatedHeading from '../../../components/AnimatedHeading';
import FadeIn from '../../../components/FadeIn';

export default function Hero() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const scrollToAbout = () => {
    const el = document.getElementById('about');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={rootRef} id="about" className="relative min-h-screen w-full overflow-hidden bg-black text-white">
      {/* Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: 'hue-rotate(252deg) saturate(0.65) brightness(0.82)' }}
      >
        <source
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260423_084718_72a17915-4964-4059-afcd-22d59399b72e.mp4"
          type="video/mp4"
        />
      </video>

      {/* Overlay — deeper for gravitas with subtle purple undertone */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50" />
      {/* Purple tonality overlay — blends video into brand palette */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#6b5b9e]/30 via-[#8b7ac4]/15 to-[#a88cd6]/25 mix-blend-multiply" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-6 md:px-12 lg:px-16">
          <div className="w-full max-w-6xl flex flex-col items-center text-center">
            {/* Main heading — slower, calmer reveal */}
            <AnimatedHeading
              text={'Liberty to Invest\nLogic to Build'}
              className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-normal mb-8 font-display leading-[1.02]"
              delay={300}
              charDelay={38}
            />

            {/* Subtitle */}
            <FadeIn delay={1200} duration={1400}>
              <p className="text-base md:text-xl text-white/50 max-w-2xl mb-12 leading-relaxed">
                We put capital behind the code that builds what comes next.
              </p>
            </FadeIn>

            {/* CTA */}
            <FadeIn delay={1600} duration={1400}>
              <button
                onClick={scrollToAbout}
                className="group glow-cta bg-gradient-to-br from-[#c8b8e4] via-[#9a88cc] to-[#5d4e8c] text-white px-8 py-3.5 rounded-lg font-medium hover:brightness-110 transition-all duration-500 whitespace-nowrap cursor-pointer inline-flex items-center gap-2.5 text-sm"
              >
                Learn More
                <i className="ri-arrow-down-line transition-transform duration-500 group-hover:translate-y-0.5"></i>
              </button>
            </FadeIn>
          </div>
        </div>

        {/* Scroll indicator — subtle */}
        <FadeIn delay={2400} duration={1400} className="absolute bottom-1 left-0 right-0 flex justify-center">
          <div className="flex flex-col items-center gap-2 text-white/25 text-[10px] font-mono uppercase tracking-[0.3em]">
            <span>scroll</span>
            <i className="ri-arrow-down-line text-xs"></i>
          </div>
        </FadeIn>

        {/* Glowing horizon line — brand accent */}
        <div className="absolute bottom-0 left-0 right-0 h-px pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#a88cd6]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#c8b8e4]/20 to-transparent blur-[2px]" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#9a88cc]/60 to-transparent blur-[6px]" />
        </div>
      </div>
    </section>
  );
}